import {useForm} from 'react-hook-form';
import InputMask from 'react-input-mask';
import {yupResolver} from '@hookform/resolvers/yup';
import cx from 'classnames';
import Image from 'next/image';
import {TallyErrorClassCode} from 'tally-validation';
import {object} from 'yup';

import {useCreateLabSubmissionMutation} from '#apollo/mutation/useLabSubmission';
import {useUpdateLabSubmissionMutation} from '#apollo/mutation/useLabSubmission';
import {Form, Text} from '#component/base';
import {TextInputField} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {barcodeValidator, barcodeValidatorRegex} from '#util';

import {StepProps} from '../../Register.types';
import {RegistrationStep} from '../RegistrationStep';
import {RegistrationStepBox} from '../RegistrationStepBox';

import {FieldValues, LabExistsError, SubmitHandler} from './StepBarcodeInput.types';

import * as css from './StepBarcodeInput.css';

/**
 * A barcode value will be either TH-99999 or just 999999. If the value matches one of these
 * we will parse the number out and return it as an int
 */
const parseBarcode = (value?: string) => {
  const match = value?.match(barcodeValidatorRegex);

  // The second entry will contain the barcode number when there's a match; return that
  return match?.[2];
};

export const StepBarcodeInput = ({onNext, onPrev, className, wizardData, data}: StepProps) => {
  const {track} = useAnalytics();
  const {addToast} = useToastQueue();
  const [createLabSubmission, {loading: creatingLabSubmission}] = useCreateLabSubmissionMutation();
  const [updateLabSubmission, {loading: updatingLabSubmission}] = useUpdateLabSubmissionMutation();

  // If we have a default value convert it to a string
  const initialBarcodeString = wizardData?.barcode ? `${wizardData.barcode}` : '';

  // Set up the form inline instead of `component/form/*` to make it easier to work
  // with the form within the context of this step
  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    setError,
  } = useForm<FieldValues>({
    defaultValues: {
      barcode: initialBarcodeString,
      confirmBarcode: initialBarcodeString,
    },
    mode: 'onSubmit',
    resolver: yupResolver(
      object({
        barcode: barcodeValidator.required('Please enter your vial number'),
        confirmBarcode: barcodeValidator.required('Please confirm your vial number'),
      }),
    ),
  });

  const handleSubmitBarcode: SubmitHandler = async ({barcode, confirmBarcode}) => {
    // Get the barcodes as integers
    const barcodeValue = parseBarcode(barcode);
    const confirmedValue = parseBarcode(confirmBarcode);

    // We shouldn't ever get here because the validation will check to make sure
    // the barcode matches this format. But it makes TypeScript happy, which makes me happy.
    if (!barcodeValue) {
      setError('barcode', {message: 'Invalid vial number'});
      return;
    }

    if (barcodeValue !== confirmedValue) {
      setError('confirmBarcode', {message: 'Vial numbers donʼt match'});
      return;
    }

    const barcodeChanged = initialBarcodeString !== barcodeValue;
    const registrationId = `TH${barcodeValue}`;
    let result: [Error, null] | [null, string];

    if (wizardData.labSubmissionId) {
      result = await handleUpdateLabSubmission(barcodeChanged, registrationId);
    } else {
      result = await handleCreateLabSubmission(registrationId);
    }

    const [labSubmissionErr, labSubmissionId] = result;
    if (labSubmissionErr) {
      handleLabSubmissionError(labSubmissionErr);
    } else {
      trackUserAction(barcodeChanged);
      onNext({barcode: barcodeValue, labSubmissionId});
    }
  };

  const handleUpdateLabSubmission = async (
    barcodeChanged: boolean,
    registrationId: string,
  ): Promise<[Error, null] | [null, string]> => {
    if (wizardData.labSubmissionId) {
      if (barcodeChanged) {
        try {
          const {data} = await updateLabSubmission({
            variables: {input: {labSubmissionId: wizardData.labSubmissionId, registrationId}},
          });
          return data?.labSubmission?.id
            ? [null, data.labSubmission.id]
            : [new Error('Unable to update lab submission'), null];
        } catch (err) {
          return [err as Error, null];
        }
      } else {
        return [null, wizardData.labSubmissionId];
      }
    }
    return [new Error('Lab submission ID is missing'), null];
  };

  const handleCreateLabSubmission = async (
    registrationId: string,
  ): Promise<[Error, null] | [null, string]> => {
    try {
      const {data} = await createLabSubmission({variables: {input: {registrationId}}});
      return data?.labSubmission?.id
        ? [null, data.labSubmission.id]
        : [new Error('Unable to create lab submission'), null];
    } catch (err: any) {
      if (
        err.graphQLErrors.find(
          (e: any) => e.extensions.error.classCode === TallyErrorClassCode.OBJECT_EXISTS,
        )
      ) {
        return [new LabExistsError(), null];
      }
      return [err as Error, null];
    }
  };

  const handleLabSubmissionError = (labSubmissionErr: Error | LabExistsError): void => {
    let errorMessage =
      'An error occurred. Please check your submitted values carefully and contact customer support if the issue continues.';
    if (labSubmissionErr instanceof LabExistsError) {
      errorMessage = 'This barcode has already been registered.';
    }
    addToast({
      children: errorMessage,
    });
  };

  const trackUserAction = (barcodeChanged: boolean): void => {
    if (wizardData.labSubmissionId) {
      if (barcodeChanged) {
        track<UserActionEvent>({
          evtName: EventType.UserAction,
          actionName: 'Kit Edited',
        }).catch();
      }
    } else {
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Kit Registered',
      }).catch();
    }
  };

  const hasError = Object.keys(errors).length > 0;
  const nextDisabled = isSubmitting || creatingLabSubmission || updatingLabSubmission || hasError;

  return (
    <RegistrationStep
      className={className}
      prevText="Cancel"
      nextText="Confirm"
      onNext={handleSubmit(handleSubmitBarcode)}
      onPrev={onPrev}
      isNextDisabled={nextDisabled}
      isNextLoading={isSubmitting}>
      <RegistrationStepBox className={css.box}>
        <div className={css.imageWrapper}>
          <Image
            src="/image/registration/barcode-vial.png"
            layout="fill"
            alt="barcode-vial"
            objectFit="cover"
          />
        </div>
        <div className={css.textWrapper}>
          <Text as="h2" variant="heading2" color="deepBurgundy" className={sprinkles({mB: 24})}>
            Enter your barcode
          </Text>
          <p className={sprinkles({mB: 20})}>
            <Text as="span" color="doveGray" fontWeight="semiBold">
              Locate the 6-characters under your  barcode and enter them below.
            </Text>
          </p>
          <Form
            className={cx(sprinkles({mT: [32, 48, 64]}), css.form)}
            clearErrors={clearErrors}
            errors={errors}
            onSubmit={handleSubmit(handleSubmitBarcode)}>
            <TextInputField
              as={InputMask}
              mask="TH999999"
              maskPlaceholder=""
              errors={errors}
              label="6 Character Barcode"
              placeholder="TH000000"
              {...register('barcode')}
            />
            <TextInputField
              as={InputMask}
              mask="TH999999"
              maskPlaceholder=""
              errors={errors}
              label="Re-enter Barcode"
              placeholder="TH000000"
              {...register('confirmBarcode')}
            />
          </Form>
        </div>
      </RegistrationStepBox>
    </RegistrationStep>
  );
};
